// Must run before epub.js creates underline/highlight marks (patches marks-pane).
import "../utils/patchMarksPane";
import { Book } from "epubjs";

export const loadEpubDocument = async (url) => {
  try {
    const book = new Book(url);
    await book.ready;

    if (!book.packaging) {
      await book.loaded.packaging;
    }

    if (!book.spine) {
      await book.loaded.spine;
    }

    if (!book.navigation) {
      await book.loaded.navigation;
    }

    return book;
  } catch (error) {
    console.error("Error loading EPUB document:", error);
    throw error;
  }
};

export const getEpubToc = async (epubBook) => {
  try {
    if (!epubBook) return [];
    const toc = await epubBook.navigation.toc;
    if (!toc || toc.length === 0) return [];

    return toc.map((item) => ({
      title: item.label,
      href: item.href,
      children: item.subitems ? processTocItems(item.subitems) : [],
    }));
  } catch (error) {
    console.error("Error getting EPUB TOC:", error);
    return [];
  }
};

const processTocItems = (items) => {
  if (!items || items.length === 0) return [];
  return items.map((item) => ({
    title: item.label,
    href: item.href,
    children: item.subitems ? processTocItems(item.subitems) : [],
  }));
};

export const getEpubTotalSections = async (epubBook) => {
  try {
    if (!epubBook) return 0;
    const spine = epubBook.spine;
    return spine ? spine.length : 0;
  } catch (error) {
    console.error("Error getting EPUB total sections:", error);
    return 0;
  }
};

export const getCurrentSectionIndex = (rendition) => {
  try {
    if (!rendition) return 0;
    return rendition.location?.start?.cfi ? rendition.location.start.index : 0;
  } catch (error) {
    console.error("Error getting current section index:", error);
    return 0;
  }
};

export const navigateToSection = async (rendition, sectionIndex) => {
  try {
    if (!rendition) return;
    await rendition.display(sectionIndex);
  } catch (error) {
    console.error("Error navigating to section:", error);
    throw error;
  }
};

// ── EPUB page metadata (from pmetadata.json, via buildPdfMetadata) ──────────
//
// Two supported shapes:
//
// 1) Full synthetic pagination (character-based, cross-book page numbers)
//    Required: content_type "epub", total_character_count, total_pages,
//    chapters[] with chapter_number, start_page, end_page, page_count.
//    Example: chapter 14 → start_page 220, end_page 227, page_count 8.
//    initial_view.start_page is a global synthetic page (not a device page).
//
// 2) Chapter-index pagination (legacy / simpler)
//    chapters[] with start_page ≈ chapter_number, small page_count per chapter.
//    No total_character_count → not "full synthetic", but chapter navigation
//    still uses start_page/end_page ranges from pmetadata.json.
//
// metadata.json is fallback only when pmetadata.json has no chapters[].
// Reflowable layout still varies per device; synthetic pages are stable IDs.

export const isSyntheticEpubMetadata = (metadata) =>
  metadata?.content_type === "epub" &&
  !!metadata.total_character_count &&
  Array.isArray(metadata.chapters);

/**
 * True when a stored currentPage looks like an old chapter-index save (1..N)
 * rather than a synthetic page. EPUB localStorage restore only — never use
 * for API initial_view.start_page.
 */
export const isLegacySavedEpubPage = (pageNumber, metadata) => {
  if (!isSyntheticEpubMetadata(metadata)) return false;

  const page = Number(pageNumber);
  if (!Number.isFinite(page) || page < 1) return false;

  const totalChapters =
    Number(metadata.total_chapters) || metadata.chapters.length;
  const totalPages = Number(metadata.total_pages) || 0;

  if (page > totalChapters) return false;

  if (totalPages > totalChapters) {
    const inSyntheticRange = metadata.chapters.some((chapter) => {
      const start = Number(chapter.start_page) || 1;
      const end = Number(chapter.end_page) || start;
      return page >= start && page <= end;
    });
    if (inSyntheticRange) return false;

    return metadata.chapters.some(
      (chapter) => Number(chapter.chapter_number) === page,
    );
  }

  return page <= totalChapters;
};

/** Clamp API start_page to total_pages for EPUB synthetic pagination. */
export const normalizeEpubInitialStartPage = (startPage, metadata) => {
  const page = Number(startPage);
  if (!Number.isFinite(page) || page < 1) return null;

  const maxPage = Number(metadata?.total_pages);
  if (maxPage > 0) return Math.min(page, maxPage);
  return page;
};

/**
 * Resolve API initial_view.start_page. PDF values pass through unchanged;
 * EPUB values are clamped via normalizeEpubInitialStartPage.
 */
export const resolveApiInitialStartPage = (startPage, metadata) => {
  const page = Number(startPage);
  if (!Number.isFinite(page) || page < 1) return null;

  const isEpub =
    (metadata?.content_type || "").toString().toLowerCase() === "epub";
  if (isEpub) return normalizeEpubInitialStartPage(page, metadata);
  return page;
};

export const navigateToChapter = async (epubBook, rendition, href) => {
  try {
    if (!epubBook || !rendition || !href) return -1;

    // Find the spine item that matches the href
    const spine = epubBook.spine;
    if (!spine) return -1;

    // Find the section index for the given href
    let sectionIndex = -1;
    for (let i = 0; i < spine.length; i++) {
      const spineItem = spine.get(i);
      if (spineItem && spineItem.href === href) {
        sectionIndex = i;
        break;
      }
    }

    if (sectionIndex !== -1) {
      await rendition.display(sectionIndex);
      return sectionIndex;
    } else {
      console.warn(`Chapter with href "${href}" not found in spine`);
      return -1;
    }
  } catch (error) {
    console.error("Error navigating to chapter:", error);
    throw error;
  }
};
